import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook that returns a key that changes when the page becomes visible again.
 * Useful for forcing re-render of components (like video players) that may
 * become stale when the app loses focus (e.g., when native YouTube app opens on iOS).
 *
 * @param onVisibilityReturn - Optional callback to execute when visibility returns
 */
export const useVisibilityRerender = (onVisibilityReturn?: () => void) => {
    const [rerenderKey, setRerenderKey] = useState(0);
    const callbackRef = useRef(onVisibilityReturn);

    // Keep the callback ref up to date
    useEffect(() => {
        callbackRef.current = onVisibilityReturn;
    }, [onVisibilityReturn]);

    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible') {
            setRerenderKey((prev) => prev + 1);
            callbackRef.current?.();
        }
    }, []);

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleVisibilityChange]);

    return rerenderKey;
};
